import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Chip, Paper, Divider } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useParams } from 'react-router-dom';

const BRAND_GRADIENT = 'linear-gradient(to right, #00CCCC, #FFC840, #FFB000, #CD00FF, #DD50FF, #00B2AD)';

interface CertRecord { learnerName: string; courseName: string; date: string; }

const CertVerifyPage: React.FC = () => {
  const params = useParams<{ code: string }>();
  const code = params?.code;
  const [record, setRecord] = useState<CertRecord | null | 'loading'>('loading');

  useEffect(() => {
    if (!code) { setRecord(null); return; }
    try {
      const idx = JSON.parse(localStorage.getItem('sedstart_certs_index') || '{}');
      setRecord(idx[code] ?? null);
    } catch {
      setRecord(null);
    }
  }, [code]);

  if (record === 'loading') return null;

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      {/* Top gradient bar */}
      <Box sx={{ height: 6, background: BRAND_GRADIENT, borderRadius: '4px 4px 0 0' }} />

      <Paper elevation={3} sx={{ borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          {record ? (
            <>
              {/* Verified icon */}
              <Box sx={{
                width: 88, height: 88, borderRadius: '50%', bgcolor: '#E8F5E9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3,
              }}>
                <CheckCircle sx={{ fontSize: 56, color: '#2E7D32' }} />
              </Box>

              <Chip
                label="Verified Certificate"
                color="success"
                icon={<CheckCircle />}
                sx={{ mb: 3, fontWeight: 700, fontSize: 14, px: 1 }}
              />

              <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                {record.learnerName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                has successfully completed
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: '#111' }}>
                {record.courseName}
              </Typography>

              <Box sx={{
                display: 'inline-flex', alignItems: 'center', gap: 1,
                bgcolor: '#F1F8E9', border: '1px solid #AED581', borderRadius: 2, px: 2, py: 1, mb: 2,
              }}>
                <CheckCircle sx={{ fontSize: 16, color: '#558B2F' }} />
                <Typography variant="body2" fontWeight={600} color="#558B2F">
                  Issued on {record.date}
                </Typography>
              </Box>

              <Divider sx={{ mt: 3, mb: 2 }} />

              <Typography variant="caption" color="text.secondary">
                Certificate ID: <strong>{code}</strong>
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Issued by Sedstart Learning Platform
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{
                width: 88, height: 88, borderRadius: '50%', bgcolor: '#FFEBEE',
                display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3,
              }}>
                <Cancel sx={{ fontSize: 56, color: 'error.main' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>Certificate Not Found</Typography>
              <Typography color="text.secondary" variant="body2">
                The certificate with code <strong>{code}</strong> could not be verified.
                <br />
                Please check the code and try again.
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
        Sedstart Certificate Verification Portal
      </Typography>
    </Container>
  );
};

export default CertVerifyPage;
